/**
 * Subcontractor Articles Page
 * Shows all articles with the subcontractor's prices from their price list
 */

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Package, Search, Loader2, Leaf, Tag } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { usePageTracking } from '../../hooks/usePageTracking'

interface Article {
  id: string
  article_number: string
  name: string
  description: string | null
  category: string | null
  type: string
  unit: string
  myPrice: number | null
}

interface Category {
  id: string
  name: string
  color: string
}

const articleTypes = [
  { key: 'service', name: 'Tjänst' },
  { key: 'product', name: 'Produkt' },
  { key: 'hourly', name: 'Timdebitering' }
]

export function SubcontractorArticles() {
  const { user } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedType, setSelectedType] = useState<string>('all')

  // Track page view
  usePageTracking('articles')

  // Fetch subcontractor with their price list
  const { data: subcontractor } = useQuery({
    queryKey: ['my-subcontractor-with-pricelist', user?.id],
    queryFn: async () => {
      if (!user?.id) return null
      const { data, error } = await supabase
        .from('subcontractors')
        .select('id, name, price_list_id')
        .eq('user_id', user.id)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!user?.id
  })

  // Fetch categories
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['article-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('article_categories')
        .select('id, name, color')
        .eq('is_active', true)
        .order('sort_order')
      if (error) throw error
      return data || []
    }
  })

  // Fetch articles with subcontractor's price list prices using secure RPC
  const { data: articles = [], isLoading } = useQuery<Article[]>({
    queryKey: ['subcontractor-articles', subcontractor?.id],
    queryFn: async () => {
      if (!subcontractor?.id) return []

      // Use RPC function that securely returns only TG's own prices
      const { data, error } = await supabase
        .rpc('get_articles_for_subcontractor')

      if (error) throw error

      return (data || []).map((a: any) => ({
        id: a.id,
        article_number: a.article_number,
        name: a.name,
        description: a.description,
        category: a.category,
        type: a.type || 'service',
        unit: a.unit,
        myPrice: a.my_price ?? null
      }))
    },
    enabled: !!subcontractor?.id
  })

  // Helper functions
  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return 'Ingen kategori'
    const category = categories.find(c => c.id === categoryId)
    return category?.name || 'Okänd kategori'
  }

  const getCategoryColor = (categoryId: string | null) => {
    if (!categoryId) return '#6B7280'
    const category = categories.find(c => c.id === categoryId)
    return category?.color || '#6B7280'
  }

  const getTypeName = (type: string) => {
    const typeObj = articleTypes.find(t => t.key === type)
    return typeObj?.name || type
  }

  // Filter articles
  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      // Search filter
      const matchesSearch = searchTerm === '' ||
        article.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (article.description && article.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        article.article_number.toLowerCase().includes(searchTerm.toLowerCase())

      // Category filter
      const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory

      // Type filter
      const matchesType = selectedType === 'all' || article.type === selectedType

      return matchesSearch && matchesCategory && matchesType
    })
  }, [articles, searchTerm, selectedCategory, selectedType])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">Artiklar</h1>
        <p className="text-nature-600 dark:text-nature-400">
          {subcontractor?.name
            ? `Artiklar och priser för ${subcontractor.name}`
            : 'Se alla artiklar och dina priser'}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
          <input
            type="text"
            placeholder="Sök artiklar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Category filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100"
        >
          <option value="all">Alla kategorier</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>

        {/* Type filter */}
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100"
        >
          <option value="all">Alla typer</option>
          {articleTypes.map(type => (
            <option key={type.key} value={type.key}>{type.name}</option>
          ))}
        </select>
      </div>

      {/* Results count */}
      <div className="text-sm text-nature-500 dark:text-nature-400">
        Visar {filteredArticles.length} av {articles.length} artiklar
      </div>

      {/* Articles list */}
      {filteredArticles.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-nature-800 rounded-lg border border-nature-200 dark:border-nature-700">
          <Package className="mx-auto h-12 w-12 text-nature-400" />
          <h3 className="mt-4 text-lg font-medium text-nature-900 dark:text-nature-100">
            {searchTerm || selectedCategory !== 'all' || selectedType !== 'all'
              ? 'Inga artiklar matchar filtret'
              : 'Inga artiklar'}
          </h3>
          <p className="mt-2 text-nature-500 dark:text-nature-400">
            {searchTerm || selectedCategory !== 'all' || selectedType !== 'all'
              ? 'Prova att ändra sökning eller filter'
              : 'Det finns inga artiklar att visa'}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 overflow-hidden">
          {/* Mobile view */}
          <div className="lg:hidden divide-y divide-nature-200 dark:divide-nature-700">
            {filteredArticles.map(article => (
              <div key={article.id} className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-xs font-mono text-nature-500 dark:text-nature-400 mr-2">
                      {article.article_number}
                    </span>
                    <h3 className="font-medium text-nature-900 dark:text-nature-100">
                      {article.name}
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                      <Leaf className="h-4 w-4" />
                      {article.myPrice !== null
                        ? `${article.myPrice.toLocaleString('sv-SE')} kr`
                        : '-'}
                    </div>
                    <span className="text-xs text-nature-500">/{article.unit}</span>
                  </div>
                </div>
                {article.description && (
                  <p className="text-sm text-nature-600 dark:text-nature-400 mb-2 line-clamp-2">
                    {article.description}
                  </p>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="px-2 py-0.5 text-xs rounded-full text-white font-medium"
                    style={{ backgroundColor: getCategoryColor(article.category) }}
                  >
                    {getCategoryName(article.category)}
                  </span>
                  <span className="px-2 py-0.5 text-xs rounded-full bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300">
                    {getTypeName(article.type)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table view */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-nature-50 dark:bg-nature-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                    Nr
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                    Artikel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                    Beskrivning
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      Kategori
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                    Typ
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                    <div className="flex items-center justify-end gap-1">
                      <Leaf className="h-3 w-3 text-green-500" />
                      Mitt pris
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nature-200 dark:divide-nature-700">
                {filteredArticles.map(article => (
                  <tr key={article.id} className="hover:bg-nature-50 dark:hover:bg-nature-700/50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-xs font-mono text-nature-600 dark:text-nature-400">
                        {article.article_number}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-nature-900 dark:text-nature-100">
                        {article.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-nature-600 dark:text-nature-400 max-w-xs truncate">
                      {article.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className="px-2 py-0.5 text-xs rounded-full text-white font-medium"
                        style={{ backgroundColor: getCategoryColor(article.category) }}
                      >
                        {getCategoryName(article.category)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300">
                        {getTypeName(article.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="font-medium text-green-600 dark:text-green-400">
                        {article.myPrice !== null
                          ? `${article.myPrice.toLocaleString('sv-SE')} kr`
                          : '-'}
                      </span>
                      <span className="text-xs text-nature-500 ml-1">/{article.unit}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info about missing prices */}
      {!subcontractor?.price_list_id && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Du har ingen prislista kopplad. Kontakta administratören för att få dina priser inställda.
          </p>
        </div>
      )}
    </div>
  )
}
